export enum ConsumptionDashboardEvent {
    /**
     * Fired when a consumption dashboard page is mounted.
     * Common properties: { UnitType, UserRole, PageScope, TenantId?, FolderKey?, IsCustomDateRange }
     */
    PageViewed = 'ConsumptionDashboard.PageViewed',
    /**
     * Fired when the user navigates away from a consumption dashboard page.
     * Properties: { ...Common, DurationMs: number }
     */
    PageSessionEnded = 'ConsumptionDashboard.PageSessionEnded',
    /**
     * Fired when the user confirms an export action.
     * Properties: { ...Common,
     *   ExportType: 'ConsumptionSummary' | 'DistributionOfConsumption' |
     *     'ConsumptionByService' | 'ConsumptionAnalyticsCustomExport' |
     *     'FolderConsumption' | 'ProcessConsumption',
     *   ExportScope: 'Organization' | 'Tenant' | 'Folder',
     *   TimeGranularity?: 'Day' | 'Week' | 'Month',
     *   SelectedServicesCount?: number,
     *   TotalServicesCount?: number }
     */
    ExportStarted = 'ConsumptionDashboard.ExportStarted',
    /**
     * Fired when the stop-usage dialog is opened.
     * Properties: { ...Common, CurrentStoppedServicesCount: number, TotalServicesCount: number }
     */
    StopUsageDialogOpened = 'ConsumptionDashboard.StopUsageDialogOpened',
    /**
     * Fired when the user saves changes in the stop-usage dialog.
     * Properties: { ...Common,
     *   PreviousStoppedServicesCount: number,
     *   NewStoppedServicesCount: number,
     *   TotalServicesCount: number,
     *   ServicesAdded: number,
     *   ServicesRemoved: number }
     */
    StopUsageModified = 'ConsumptionDashboard.StopUsageModified',
    /**
     * Fired when the allocation dialog is opened.
     * Properties: { ...Common, TenantId: string, CurrentAllocation: number, AvailableToAllocate: number }
     */
    AllocationDialogOpened = 'ConsumptionDashboard.AllocationDialogOpened',
    /**
     * Fired when the user saves an allocation change.
     * Properties: { ...Common,
     *   TenantId: string,
     *   PreviousAllocation: number,
     *   NewAllocation: number,
     *   AllocationDelta: number,
     *   RuFallbackEnabled?: boolean }
     */
    AllocationSaved = 'ConsumptionDashboard.AllocationSaved',
    /**
     * Fired when the user toggles between Ownership and Execution view.
     * Properties: { ...Common,
     *   ToggleValue: 'Ownership' | 'Execution',
     *   Section: 'FolderSummary' | 'FolderConsumption' | 'ProcessConsumption' }
     */
    BreakdownToggleClicked = 'ConsumptionDashboard.BreakdownToggleClicked',
    /**
     * Fired when the user clicks the redirect to notification settings.
     * Properties: { ...Common }
     */
    NotificationSettingsClicked = 'ConsumptionDashboard.NotificationSettingsClicked',
    /**
     * Fired when the user applies a custom date range.
     * Properties: { ...Common, StartDate: string, EndDate: string, RangeDays: number }
     */
    CustomDateRangeApplied = 'ConsumptionDashboard.CustomDateRangeApplied',
    /**
     * Fired when the user clicks a process link to navigate to Orchestrator/Maestro.
     * Properties: { ...Common, ProcessType: string }
     */
    ProcessLinkClicked = 'ConsumptionDashboard.ProcessLinkClicked',
    /**
     * Fired when the user uses search in any section (debounced, no search value included).
     * Properties: { ...Common, Section: 'TenantAllocations' | 'FolderConsumption' | 'ProcessConsumption', ResultCount: number }
     */
    SearchUsed = 'ConsumptionDashboard.SearchUsed',
    /**
     * Fired when the user changes a filter in any section.
     * Properties: { ...Common,
     *   Section: 'TenantAllocations' | 'FolderConsumption' | 'ProcessConsumption',
     *   FilterType: 'Status' | 'Folder' | 'ProcessType',
     *   FilterValue: string }
     */
    FilterChanged = 'ConsumptionDashboard.FilterChanged',
    /**
     * Fired when a process export hits the 2000 row limit.
     * Properties: { ...Common, TotalCount: number }
     */
    ExportLimitReached = 'ConsumptionDashboard.ExportLimitReached',
    /**
     * Fired when the user confirms exporting at the 2000 row limit.
     * Properties: { ...Common, TotalCount: number }
     */
    ExportLimitConfirmed = 'ConsumptionDashboard.ExportLimitConfirmed',
    /**
     * Fired when the user clicks the documentation floating button.
     * Properties: { ...Common }
     */
    DocumentationClicked = 'ConsumptionDashboard.DocumentationClicked',
    /**
     * Fired when the Process view of the Solutions & Processes unit breakdown popover is shown
     * (either on open with Process as the initial view, or after toggling to Process).
     * Properties: { ...Common }
     */
    ProcessBreakdownTooltipViewed = 'ConsumptionDashboard.ProcessBreakdownTooltipViewed',
    /**
     * Fired when the Service view of the Solutions & Processes unit breakdown popover is shown
     * (either on open with Service as the initial view, or after toggling to Service).
     * Properties: { ...Common }
     */
    ServiceBreakdownTooltipViewed = 'ConsumptionDashboard.ServiceBreakdownTooltipViewed',
}
